import { garmentStyleNumbers, type GarmentLike, type PrintPositionLike } from './store-template-kinds';

// Catalog cards + style→photo lookup for Product lineups.
// Photos come from /api/lineup-garment-thumbs (one URL per style). A garment
// may list several style aliases (GSF101 / PC78H / 1254); any of them can hit.

export type PrintLocation = {
  position: string;
  label: string;
  size: string;
  code: string;
};

export type LineupPickerCard = {
  key: string;
  styleNumber: string;
  name: string;
  brand: string;
  category: string;
  productType: string;
  fabric: string;
  decoration: string;
  aliases: string[];
  aliasLabels: string[];
  sizes: string;
  tiers: string;
  locations: PrintLocation[];
  image: string;
};

const VENDOR_LABEL: Record<string, string> = {
  ghostfit: 'GhostFit',
  sanmar: 'SanMar',
  ss: 'S&S',
  's&s': 'S&S',
  nextlevel: 'Next Level',
  adidas: 'Adidas',
  nike: 'Nike',
};

function titleCase(s: string): string {
  return String(s || '').replace(/[-_]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()).trim();
}

export function formatPrintSize(width?: number, height?: number): string {
  if (!width || !height) return '';
  return `${width}×${height}"`;
}

export function formatPrintLocations(positions: PrintPositionLike[] | undefined): PrintLocation[] {
  return (positions || []).map((p) => {
    const label = p.display || titleCase(p.position || p.code || '');
    const position = String(p.position || '').trim()
      || String(label || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    return {
      position,
      label,
      size: formatPrintSize(p.printWidth, p.printHeight),
      code: String(p.code || '').toUpperCase(),
    };
  }).filter((p) => p.label && p.position);
}

export function formatSizeRange(sizes: string[] | undefined): string {
  const list = (sizes || []).filter(Boolean);
  if (!list.length) return '';
  const youth = list.filter((s) => /^Y/i.test(s));
  const adult = list.filter((s) => !/^Y/i.test(s));
  const parts: string[] = [];
  if (youth.length) parts.push(youth.length === 1 ? youth[0] : `${youth[0]}–${youth[youth.length - 1]}`);
  if (adult.length) parts.push(adult.length === 1 ? adult[0] : `${adult[0]}–${adult[adult.length - 1]}`);
  return parts.join(' · ');
}

export function formatTiers(tiers: Record<string, { price?: number }> | undefined): string {
  return Object.entries(tiers || {})
    .filter(([, t]) => typeof t?.price === 'number')
    .map(([name, t]) => `${titleCase(name)} $${t.price}`)
    .join(' · ');
}

export function formatStyleAliases(styleNumbers: Record<string, string> | undefined, fallback?: string): string[] {
  const entries = Object.entries(styleNumbers || {}).filter(([, n]) => !!n);
  if (!entries.length) return fallback ? [fallback] : [];
  return entries.map(([vendor, n]) => {
    const label = VENDOR_LABEL[vendor.toLowerCase()] || titleCase(vendor);
    return `${label} ${n}`;
  });
}

export function thumbForAliases(aliases: string[], thumbs: Record<string, string>): string {
  for (const a of aliases) {
    const hit = thumbs[String(a || '').toUpperCase()];
    if (hit) return hit;
  }
  return '';
}

export function lineupPickerCards(
  garments: GarmentLike[],
  thumbs: Record<string, string>,
): LineupPickerCard[] {
  return garments
    .map((g) => {
      const aliases = garmentStyleNumbers(g);
      const styleNumber = g.styleNumber
        || g.styleNumbers?.ghostfit
        || aliases[0]
        || g.id
        || '';
      return {
        key: g.id || styleNumber,
        styleNumber,
        name: g.name || styleNumber,
        brand: g.supplier || '',
        category: g.category || '',
        productType: g.productType || '',
        fabric: g.fabricComposition || '',
        decoration: g.decorationMethod || '',
        aliases,
        aliasLabels: formatStyleAliases(g.styleNumbers, styleNumber),
        sizes: formatSizeRange(g.sizes),
        tiers: formatTiers(g.tiers),
        locations: formatPrintLocations(g.printPositions),
        image: thumbForAliases([...aliases, styleNumber], thumbs),
      };
    })
    .filter((c) => !!c.styleNumber)
    .sort((a, b) => a.name.localeCompare(b.name) || a.styleNumber.localeCompare(b.styleNumber));
}

export function findPickerCard(cards: LineupPickerCard[], styleNumber: string): LineupPickerCard | undefined {
  const n = String(styleNumber || '').toUpperCase();
  if (!n) return undefined;
  return cards.find((c) => c.styleNumber.toUpperCase() === n || c.aliases.some((a) => a.toUpperCase() === n));
}

export function filterPickerCards(
  cards: LineupPickerCard[],
  query: string,
  category: string,
): LineupPickerCard[] {
  const q = query.trim().toLowerCase();
  return cards.filter((c) => {
    if (category && c.category !== category) return false;
    if (!q) return true;
    const hay = [
      c.name, c.styleNumber, c.brand, c.productType, c.category,
      c.aliases.join(' '), c.locations.map((l) => `${l.label} ${l.code}`).join(' '),
    ].join(' ').toLowerCase();
    return hay.includes(q);
  });
}
